import { API_PREFIX_LEGACY } from '@/const/api';
import type { Button } from '@lemonade-hq/nuis';
import { Client, useAnalyticsContext } from '@lemonade-hq/nuiza';
import { isValidEmail } from '@lemonade-hq/ts-helpers';
import type React from 'react';
import { useEffect, useState } from 'react';
import useForm, {
  basicRequiredValidation,
  type UseFormConfig,
  type UseFormErrorData,
  type UseFormFieldConfig,
} from './useForm';

const URL = `/${API_PREFIX_LEGACY}/contact_us`;

interface AuaState {
  thankYou: boolean;
  submitted: boolean;
  submittedOnce: boolean;
  serverError: string;
  displayedErrors: string[];
}

export interface UseAuaStateProps {
  closeDialog?: () => void;
  validation: {
    name: string;
    email: string;
    message: string;
    general: string;
  };
  trackingProps?: Record<string, string>;
}

export interface UseAuaStateReturnType {
  displayedErrors: string[];
  auaState: AuaState;
  setValueOnChange: (value: string) => (e: React.ChangeEvent) => void;
  errors?: Record<string, UseFormErrorData>;
  values: Record<string, string>;
  onClose: () => void;
  sendButtonProps: Partial<React.ComponentProps<typeof Button>>;
}

export const useAuaState = ({
  closeDialog,
  validation,
  trackingProps = {},
}: UseAuaStateProps): UseAuaStateReturnType => {
  const [displayedErrors, setDisplayedErrors] = useState<string[]>([]);
  const { trackEvent } = useAnalyticsContext();
  const [auaState, setAuaState] = useState<AuaState>({
    thankYou: false,
    submitted: false,
    submittedOnce: false,
    serverError: '',
    displayedErrors: [],
  });

  const { thankYou, submitted } = auaState;

  const config: UseFormConfig = {
    validateOnSubmit: true,
    submitted: auaState.submitted,
    fields: [
      {
        name: 'name',
        startValue: '',
        validations: {
          required: basicRequiredValidation,
          atLeastTwo: {
            errorMessage: validation.name,
            test: (value: string) => {
              return value.length >= 2;
            },
          },
        },
      },
      {
        name: 'email',
        startValue: '',
        validations: {
          required: basicRequiredValidation,
          validEmail: {
            errorMessage: validation.email,
            test: isValidEmail,
          },
        },
      },
      {
        name: 'question',
        startValue: '',
        validations: {
          required: basicRequiredValidation,
          atLeastTwo: {
            errorMessage: validation.message,
            test: (value: string) => {
              return value.length >= 2;
            },
          },
        },
      },
    ],
  };

  const { valid, enabled, errors, reset, setValue, values } = useForm(config);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setAuaState(prevState => ({
      ...prevState,
      submitted: false,
    }));
  }, [values]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    setDisplayedErrors(
      config.fields.reduce((acc, field: UseFormFieldConfig) => {
        let resp: string[] = acc;

        if (errors?.[field.name]) {
          const messages = Object.keys(field.validations).reduce<string[]>((msgs, val) => {
            const validation = field.validations[val as keyof UseFormFieldConfig];
            if (errors[field.name][val]) {
              msgs.push(validation.errorMessage ?? '');
            }
            return msgs;
          }, [] as string[]);

          resp = [...resp, ...messages];
        }
        return resp;
      }, [] as string[]),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const submitAua = async (e: React.FormEvent<HTMLFormElement>, page?: string): Promise<void> => {
    e.preventDefault();
    setAuaState(prevState => ({
      ...prevState,
      submitted: true,
      submittedOnce: true,
    }));

    const valuesToSubmit = {
      ...values,

      context: {
        name: 'marketing_website_aua',
        data: {},
        landing_page: page ?? '',
        action: 'help_button',
      },
    };

    if (valid) {
      trackEvent('Ask_us_anything_popup.submitted', trackingProps);
      try {
        await Client.post(`${URL}/`, valuesToSubmit);
        setAuaState(prevState => ({
          ...prevState,
          thankYou: true,
          submitted: false,
        }));
      } catch (e: unknown) {
        console.log(e);
        setAuaState(prevState => ({
          ...prevState,
          submitted: false,
          serverError: validation.general,
        }));
      }
    }
  };

  const setValueOnChange = (keyName: string) => (e: React.ChangeEvent) => {
    setAuaState(prevState => ({
      ...prevState,
      serverError: '',
    }));
    setValue(keyName, (e.target as HTMLInputElement).value ?? '');
  };

  const onClose = (): void => {
    setAuaState(prevState => ({
      ...prevState,
      thankYou: false,
      submitted: false,
      serverError: '',
    }));
    reset();
    if (closeDialog) {
      closeDialog();
    }
  };

  const sendButtonProps = {
    disabled: !enabled,
    glowing: true,
    onClick: submitAua,
    loading: valid && submitted && !thankYou,
  };

  return {
    displayedErrors,
    auaState,
    setValueOnChange,
    errors,
    values,
    onClose,
    sendButtonProps,
  };
};
